import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import './Contact.css'

const API = process.env.REACT_APP_API_URL || "";



const FIELDS = [
    { id: 'name', label: 'Full Name', type: 'text', span: 1 },
    { id: 'phone', label: 'Phone Number', type: 'tel', span: 1 },
    { id: 'email', label: 'Email Address', type: 'email', span: 2 },
    { id: 'subject', label: 'Subject', type: 'text', span: 2 },
    { id: 'message', label: 'Message', type: 'area', span: 2 },
]

export default function Contact() {
    const sectionRef = useRef(null)
    const headRef = useRef(null)
    const formRef = useRef(null)
    const fieldRefs = useRef([])

    const [focused, setFocused] = useState(null)
    const [values, setValues] = useState({})
    const [fieldErrors, setFieldErrors] = useState({}) // Stores specific errors like { phone: "Invalid..." }
    const [sent, setSent] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [generalError, setGeneralError] = useState(null)

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(headRef.current,
                { opacity: 0, y: 60 },
                {
                    opacity: 1, y: 0, duration: 1, ease: 'expo.out',
                    scrollTrigger: { trigger: headRef.current, start: 'top 85%' }
                }
            )

            if (fieldRefs.current) {
                fieldRefs.current.forEach((el, i) => {
                    if (!el) return
                    gsap.fromTo(el,
                        { opacity: 0, y: 40 },
                        {
                            opacity: 1, y: 0, duration: 0.7, ease: 'expo.out',
                            delay: i * 0.07,
                            scrollTrigger: { trigger: formRef.current, start: 'top 80%' }
                        }
                    )
                })
            }
        }, sectionRef)
        return () => ctx.revert()
    }, [])

    const validateFields = () => {
        const errors = {}

        // Name
        if (!values.name || values.name.trim().length < 2) {
            errors.name = "Name must be at least 2 characters"
        }

        // Phone (10–15 digits, optional +)
        if (!values.phone) {
            errors.phone = "Phone number is required"
        } else if (!/^\+?[0-9]\d{9,14}$/.test(values.phone)) {
            errors.phone = "Enter a valid 10-15 digit phone number"
        }

        // Email
        if (!values.email) {
            errors.email = "Email is required"
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
            errors.email = "Please enter a valid email address"
        }

        // Subject
        if (!values.subject || values.subject.trim().length < 2) {
            errors.subject = "Subject is required"
        }

        // Message
        if (!values.message || values.message.trim().length < 10) {
            errors.message = "Message must be at least 10 characters long"
        }

        return errors
    }

    const handleChange = (id, val) => {
        setValues(p => ({ ...p, [id]: val }))
        // Clear error for this specific field as soon as user starts typing again
        if (fieldErrors[id]) {
            setFieldErrors(prev => {
                const updated = { ...prev }
                delete updated[id]
                return updated
            })
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        const errors = validateFields()

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors)
            return
        }

        setIsSubmitting(true)
        setGeneralError(null)

        try {
            const response = await fetch(`${API}/api/contacts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values),
            })

            const data = await response.json()

            if (!response.ok) {
                if (data.errors) {
                    setFieldErrors(data.errors)
                } else {
                    setGeneralError(data.message || 'Transmission failed.')
                }
                return
            }

            gsap.to(formRef.current, {
                opacity: 0,
                y: -20,
                duration: 0.5,
                onComplete: () => {
                    setSent(true)
                    setValues({})
                }
            })

        } catch (err) {
            setGeneralError("Connection error. Is the server running?")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <section ref={sectionRef} className="ctf-wrap">
            <div className="ctf-bg-grid" />
            <div className="ctf-label"><span className="ctf-label-dot" /> CONTACT US</div>

            <div className="ctf-inner">
                <div ref={headRef} className="ctf-head">
                    <span className="ctf-eyebrow">Let's Talk</span>
                    <h2 className="ctf-title">
                        <span>GET</span><span>IN</span><span className="ctf-title-outline">TOUCH.</span>
                    </h2>
                    <p className="ctf-desc">We’d love to hear from you. Tell us what you need.</p>
                </div>

                <div className="ctf-form-wrap">
                    {!sent ? (
                        <form ref={formRef} className="ctf-form" onSubmit={handleSubmit} noValidate>
                            {generalError && <div className="ctf-general-error">{generalError}</div>}

                            <div className="ctf-grid">
                                {FIELDS.map((f, i) => (
                                    <div
                                        key={f.id}
                                        ref={el => { if (fieldRefs.current) fieldRefs.current[i] = el }}
                                        className={`ctf-field ctf-field--span${f.span} ${focused === f.id || values[f.id] ? 'ctf-field--active' : ''}`}
                                    >
                                        <label className="ctf-field-label" htmlFor={f.id}>{f.label}</label>

                                        {f.type === 'area' ? (
                                            <textarea
                                                id={f.id}
                                                className="ctf-input ctf-textarea"
                                                placeholder={f.placeholder}
                                                value={values[f.id] || ''}
                                                onFocus={() => setFocused(f.id)}
                                                onBlur={() => setFocused(null)}
                                                onChange={e => handleChange(f.id, e.target.value)}
                                            />
                                        ) : (
                                            <input
                                                id={f.id}
                                                type={f.type}
                                                className="ctf-input"
                                                placeholder={f.placeholder}
                                                value={values[f.id] || ''}
                                                onFocus={() => setFocused(f.id)}
                                                onBlur={() => setFocused(null)}
                                                onChange={e => handleChange(f.id, e.target.value)}
                                            />
                                        )}

                                        {/* FIELD-LEVEL ERROR MESSAGE */}
                                        {fieldErrors[f.id] && (
                                            <span className="ctf-field-error-msg">
                                                {fieldErrors[f.id]}
                                            </span>
                                        )}

                                        <div className="ctf-field-line" />
                                    </div>
                                ))}
                            </div>

                            <button type="submit" className="ctf-submit" disabled={isSubmitting}>
                                <span>{isSubmitting ? 'Verifying...' : 'Send Message'}</span>
                                {!isSubmitting && <span className="ctf-submit-arrow">→</span>}
                            </button>
                        </form>
                    ) : (
                        <div className="ctf-success">
                            <div className="ctf-success-icon">✓</div>
                            <h3>Message Sent.</h3>
                            <p>Check your inbox for confirmation.</p>
                            <p>We will get back to you soon.</p>
                            <button onClick={() => setSent(false)} className="ctf-retry-btn">Send Another</button>
                        </div>
                    )}
                </div>
            </div>
            <div className="ctf-watermark">CONTACT</div>
        </section>
    )
}